/**
 * Wires the two-step link callout into a roadmap map: link items open a
 * callout on the first press and follow the link on the second, and every
 * other item calls the map's `onSelect` straight away.
 */
import {
  RoadmapLinkCallout,
  roadmapLinkTriggerProps,
  useRoadmapLinkCallout,
} from 'course/splash/components/RoadmapLinkCallout';
import {useCallback, type MouseEvent, type ReactNode} from 'react';
import type {ExpeditionItem} from './expeditionModel';

/** What {@link useMapLinkCallout} hands a map. */
export interface MapLinkCallout {
  /** The press handler for any item's button. */
  select: (item: ExpeditionItem, event: MouseEvent<HTMLElement>) => void;
  /**
   * The disclosure attributes for an item's button: `aria-expanded` (and
   * `aria-controls` while open) for a link item, none for any other item.
   */
  disclosureFor: (item: ExpeditionItem) => {
    'aria-expanded'?: boolean;
    'aria-controls'?: string;
  };
  /** Whether the item is a link that opens a callout. */
  isCalloutLink: (item: ExpeditionItem) => boolean;
  /**
   * The item's callout while it is open, positioned below the item, or null.
   *
   * @param args.item - The item.
   * @param args.centerX - The item's horizontal centre on the canvas.
   * @param args.top - Where the callout's top edge goes on the canvas.
   * @param args.boundsWidth - The canvas width the callout must stay inside.
   */
  calloutFor: (args: {
    item: ExpeditionItem;
    centerX: number;
    top: number;
    boundsWidth: number;
  }) => ReactNode;
}

/**
 * Whether an item is a link with somewhere to go, and so opens a callout.
 *
 * @param item - The map item.
 * @return True for a `link` item with an `href`.
 * @throws Never.
 */
function hasCallout(item: ExpeditionItem): boolean {
  return item.kind === 'link' && Boolean(item.href);
}

/**
 * Two-step link presses for a map.
 *
 * @param args.onSelect - The map's select handler; it follows a link and
 *   opens every other item.
 * @return The press handler, disclosure attributes, and callout renderer.
 * @throws Never.
 */
export function useMapLinkCallout({
  onSelect,
}: {
  onSelect: (item: ExpeditionItem) => void;
}): MapLinkCallout {
  const {openId, press, close} = useRoadmapLinkCallout();

  const select = useCallback(
    (item: ExpeditionItem, event: MouseEvent<HTMLElement>) => {
      if (!hasCallout(item)) {
        onSelect(item);
        return;
      }
      press({
        id: item.id,
        trigger: event.currentTarget,
        follow: () => onSelect(item),
      });
    },
    [onSelect, press],
  );

  const disclosureFor = useCallback(
    (item: ExpeditionItem) =>
      hasCallout(item)
        ? roadmapLinkTriggerProps({itemId: item.id, isOpen: openId === item.id})
        : {},
    [openId],
  );

  const calloutFor = useCallback<MapLinkCallout['calloutFor']>(
    ({item, centerX, top, boundsWidth}) => {
      if (openId !== item.id || !item.href) {
        return null;
      }
      return (
        <RoadmapLinkCallout
          itemId={item.id}
          title={item.title}
          description={item.blurb}
          href={item.href}
          centerX={centerX}
          top={top}
          boundsWidth={boundsWidth}
          onFollow={() => {
            close();
            onSelect(item);
          }}
          onClose={close}
        />
      );
    },
    [openId, close, onSelect],
  );

  return {select, disclosureFor, isCalloutLink: hasCallout, calloutFor};
}
